// Default upload adapter targeting Rails' TempAttachmentsController
// (app/controllers/temp_attachments_controller.rb). Posts one file as
// multipart/form-data via XMLHttpRequest so upload-progress events are
// available (fetch cannot report upload progress), wiring the renderer's
// AbortSignal to xhr.abort().
//
// The controller's #create finds the uploaded file anywhere in the params and
// renders, on success, `{ files: [to_jq_upload] }` with a text/plain content
// type and 201; on failure, `{ files: [{ name, size, error }], error }`. Both
// shapes are parsed here.

import type { UploadAdapter, UploadFileNode } from '../types'

export interface TempAttachmentAdapterOptions {
  /** Endpoint that accepts the multipart POST. Defaults to "/temp_attachments". */
  url?: string
  /** Rails CSRF token, sent as the X-CSRF-Token header. */
  csrfToken?: string
  /** Multipart field name for the file. Defaults to "files[]" (legacy jQuery-fileupload name). */
  fieldName?: string
}

interface TempAttachmentResponse {
  files?: Array<Partial<UploadFileNode> & { error?: string }>
  error?: string
}

/** An abort surfaced as a rejection the UploadField recognises and ignores. */
function abortError(): DOMException {
  return new DOMException('Upload aborted', 'AbortError')
}

export function createTempAttachmentUploadAdapter(
  options: TempAttachmentAdapterOptions = {},
): UploadAdapter {
  const { url = '/temp_attachments', csrfToken, fieldName = 'files[]' } = options

  return (file, _field, { onProgress, signal }) =>
    new Promise<UploadFileNode>((resolve, reject) => {
      if (signal.aborted) {
        reject(abortError())
        return
      }

      const xhr = new XMLHttpRequest()
      const form = new FormData()
      form.append(fieldName, file, file.name)

      const onAbort = () => xhr.abort()
      signal.addEventListener('abort', onAbort)
      const cleanup = () => signal.removeEventListener('abort', onAbort)

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(event.loaded, event.total)
        })
      }

      xhr.addEventListener('load', () => {
        cleanup()
        const parsed = parseResponse(xhr.responseText)
        const node = parsed.files?.[0]
        const errorMessage = parsed.error ?? node?.error
        // Rails returns 200 even for the failure shape, so inspect the body.
        if (errorMessage || node == null || node.error || node.id == null) {
          reject(new Error(errorMessage ?? 'Upload failed'))
          return
        }
        resolve(node as UploadFileNode)
      })

      xhr.addEventListener('error', () => {
        cleanup()
        reject(new Error('Upload failed'))
      })

      xhr.addEventListener('abort', () => {
        cleanup()
        reject(abortError())
      })

      xhr.open('POST', url)
      xhr.setRequestHeader('Accept', 'application/json, text/plain')
      if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken)
      xhr.send(form)
    })
}

function parseResponse(text: string): TempAttachmentResponse {
  if (!text) return {}
  try {
    return JSON.parse(text) as TempAttachmentResponse
  } catch {
    return { error: 'Unexpected server response' }
  }
}
